import { useCallback, useEffect, useMemo, useState } from 'react';
import { atlasClient } from '../../../api/atlasClient';
import { SparkTagDimensionSelection } from '../types/sparkTypes';
import { useAtlasStore, AtlasState } from '../../../store/atlasStore';
import { Dimension, PrismElement } from '../../../types/atlas';
import { TagPatternDisplayDimension } from '../utils/tagPatterns';

interface UseSparkTagOptionsParams {
  dimensionFqns?: Set<string> | null;
  schemaPattern?: SparkTagDimensionSelection[] | null;
}

const mapDimensionToDisplay = (
  dimension: Dimension,
  selectionMap: Map<string, SparkTagDimensionSelection>
): TagPatternDisplayDimension => {
  const selection = selectionMap.get(dimension.fqn);

  return {
    dimensionFqn: dimension.fqn,
    name: dimension.name,
    isOptional: selection?.is_optional ?? false,
    isStrict: selection?.is_strict ?? false,
    elements: dimension.elements.map((element: PrismElement) => ({
      fqn: element.fqn,
      name: element.name,
      description: element.description,
      thumbnailUrl: element.hero_image_id ? atlasClient.getAssetUrl(element.hero_image_id) : undefined,
      isActive: element.is_active,
    })),
  };
};

export const useSparkTagOptions = (
  patternSelection?: SparkTagDimensionSelection[] | null,
  params?: UseSparkTagOptionsParams
) => {
  const dimensions = useAtlasStore((state: AtlasState) => state.dimensions);
  const fetchDimensions = useAtlasStore((state: AtlasState) => state.fetchDimensions);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!dimensions || dimensions.length === 0) {
      setIsLoading(true);
      fetchDimensions()?.finally(() => setIsLoading(false));
    }
  }, [dimensions, fetchDimensions]);

  const filteredDimensions = useMemo(() => {
    if (!dimensions) {
      return [] as Dimension[];
    }
    if (params?.dimensionFqns) {
      return dimensions.filter((dimension) => params.dimensionFqns!.has(dimension.fqn));
    }
    return dimensions;
  }, [dimensions, params?.dimensionFqns]);

  const selectionMap = useMemo(() => {
    const map = new Map<string, SparkTagDimensionSelection>();
    params?.schemaPattern?.forEach(entry => map.set(entry.dimension_fqn, entry));
    patternSelection?.forEach(entry => map.set(entry.dimension_fqn, entry));
    return map;
  }, [patternSelection, params?.schemaPattern]);

  const mapToDisplay = useCallback(
    () =>
      filteredDimensions.map((dimension) =>
        mapDimensionToDisplay(dimension, selectionMap)
      ),
    [filteredDimensions, selectionMap]
  );

  const displayDimensions = useMemo<TagPatternDisplayDimension[]>(
    () => mapToDisplay(),
    [mapToDisplay]
  );

  return {
    isLoading,
    dimensions: displayDimensions,
  };
};

export default useSparkTagOptions;
